import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import config from '@/config';
import { env } from '@/env';
import { useIsAdmin } from '@/modules/auth/hooks';
import { Language, useLocale } from '@/modules/localizations';
import { selectGeneralSettings } from '@/modules/settings/services/selectors';

// The pricing embed at passwd.team is a separate codebase; its locale slugs
// are not guaranteed to match this app's Language enum. Map every app locale
// explicitly so the embed always receives a locale it supports.
const PRICING_IFRAME_LOCALE_MAP: Record<Language, string> = {
    [Language.EN]: 'en',
    [Language.CS]: 'cs',
    [Language.DE]: 'de',
    [Language.ES]: 'es',
    [Language.IT]: 'en',
    [Language.PL]: 'pl',
    [Language.RU]: 'ru',
};

export const useGetIframeUrl = () => {
    const { locale } = useLocale();

    const { userCount: workspaceUserCount, activeUserCount } = useSelector(selectGeneralSettings);

    const isAdmin = useIsAdmin();

    const urlParams = useMemo(() => {
        const params = new URLSearchParams({
            isDemo: env.VITE_DEMO ? 'true' : 'false',
            isAdmin: isAdmin ? 'true' : 'false',
        });

        if (workspaceUserCount) {
            params.append('workspaceUserCount', workspaceUserCount.toString());
        }

        if (activeUserCount) {
            params.append('activeUserCount', activeUserCount.toString());
        }

        return params;
    }, [activeUserCount, workspaceUserCount, isAdmin]);

    const iframeLocale = PRICING_IFRAME_LOCALE_MAP[locale as Language];
    const baseIframeUrl = config.embeds.pricing.replace('{locale}', iframeLocale);
    const iframeUrl = `${baseIframeUrl}?${urlParams.toString()}`;

    return iframeUrl;
};
